import { isEmail, parsePhoneNumber, ZIP_CODE_REGEX } from '@side/pantry';
import { addMonths } from 'date-fns';
import { isEmpty } from 'lodash';
import {
  derivePhoneNumber,
  isValueEqualToCountryPrefix,
} from '@/common/utils/phoneNumber';
import type { Validation } from '../configurations/types';

export const doesFieldValidationApply = (
  validation: Validation,
  cardIndex: number,
) => {
  if (!validation.appliesToIndex || isEmpty(validation.appliesToIndex))
    return true;

  return validation.appliesToIndex.includes(cardIndex);
};

export const invalidEmail = (value?: string) => {
  if (value && isEmail(value)) return false;

  return true;
};

export const isValidPhone = (value?: string | null) => {
  if (!value) {
    return true;
  }

  if (isValueEqualToCountryPrefix(value)) {
    return true;
  }

  return parsePhoneNumber(derivePhoneNumber(value)).validation.isValid;
};

export const invalidZipCode = (value?: number | string) => {
  return !value || !ZIP_CODE_REGEX.test(value.toString());
};

export const REQUIRED_MESSAGE = 'Required';
const MIN_YEAR_BUILT = 1600;
export const YEAR_MAX_LENGTH = 4;

export const getMaxYearBuilt = () => addMonths(new Date(), 18).getFullYear();

export const getYearBuiltMessage = () =>
  `Year must be between ${MIN_YEAR_BUILT} and ${getMaxYearBuilt()}`;

export const toYearDigits = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, YEAR_MAX_LENGTH);
  return digits.startsWith('0') ? '0' : digits;
};

export const toIntegerDigits = (raw: string): string => {
  // Keep only digits and dots, then take what's before the first dot.
  const wholePart = raw.replace(/[^\d.]/g, '').split('.')[0];
  // Strip leading zeros, but leave a lone "0".
  return wholePart.replace(/^0+(?=\d)/, '');
};

export const toYearNumber = (raw: string): number | null => {
  const digits = toYearDigits(raw);
  return digits ? Number(digits) : null;
};

// 0 denotes vacant land;
export const isValidYearBuilt = (
  value: string | number | null | undefined,
): boolean => {
  if (value == null || value === '') return true;
  const digits = String(value);
  if (!/^\d+$/.test(digits)) return false;
  if (digits.startsWith('0')) return digits === '0';
  const year = Number(digits);
  return year >= MIN_YEAR_BUILT && year <= getMaxYearBuilt();
};
